import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, Sparkles, Save, Loader2, FileText, MessageSquareQuote,
  RefreshCw, ClipboardList, Target, Plus, Trash2, ChevronDown, Download,
  KeyRound, Eye, EyeOff, Copy,
} from "lucide-react";

const inp = "w-full bg-[#0D0E15] border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-indigo-500/60 transition-colors";
const ARR = (v) => (Array.isArray(v) ? v.join("\n") : v || "");
const toArr = (s) => (s || "").split(/[\n,]/).map((x) => x.trim()).filter(Boolean);

const YN = ["", "Yes", "No"];
const INTAKE_SECTIONS = [
  { title: "Roles & Preferences", fields: [
    { k: "target_job_titles", label: "Target Job Titles (default — set per resume version above)", type: "textarea" },
  ]},
  { title: "Job Filters", fields: [
    { k: "preferred_locations", label: "Preferred Job Locations", type: "text" },
    { k: "experience_level", label: "Preferred Experience Level", type: "multi", options: ["Entry-level", "Associate", "Mid-level", "Senior", "Director / VP", "Open to All"] },
    { k: "employment_type", label: "Preferred Employment Type", type: "multi", options: ["Full-time", "Part-time", "Contract", "Internship", "Freelance"] },
    { k: "work_environment", label: "Preferred Work Environment", type: "multi", options: ["Onsite", "Remote", "Hybrid", "Open to All"] },
    { k: "preferred_industry", label: "Preferred Industry", type: "text" },
  ]},
  { title: "Personal Details", fields: [
    { k: "first_name", label: "First Name" }, { k: "last_name", label: "Last Name" },
    { k: "dob", label: "Date of Birth" }, { k: "address", label: "Address" },
    { k: "postal_code", label: "Postal / Zip Code" }, { k: "city", label: "City" },
    { k: "state", label: "State" }, { k: "country", label: "Country" },
    { k: "phone_code", label: "Phone Country Code" }, { k: "phone", label: "Phone Number" },
    { k: "email", label: "Email" }, { k: "linkedin", label: "LinkedIn Profile (URL)" },
    { k: "gender", label: "Gender" }, { k: "sexual_orientation", label: "Sexual Orientation" },
  ]},
  { title: "Employment Preferences", fields: [
    { k: "expected_salary", label: "Expected Salary" }, { k: "current_salary", label: "Current Salary" },
    { k: "expected_joining", label: "Expected Date of Joining" }, { k: "notice_period", label: "Notice Period" },
    { k: "languages", label: "Languages Known" }, { k: "total_experience_years", label: "Total Experience (years)" },
    { k: "where_heard", label: "Where Did You Hear About Us?" },
    { k: "willing_relocate", label: "Willing to Relocate?", type: "select", options: YN },
    { k: "require_relocation_allowance", label: "Require Relocation Allowance?", type: "select", options: YN },
    { k: "authorized_us", label: "Authorized to Work in USA?", type: "select", options: YN },
    { k: "veteran", label: "Veteran / Military?", type: "select", options: YN },
    { k: "disability", label: "Disability" }, { k: "race_ethnicity", label: "Race / Ethnicity" },
    { k: "hispanic_latino", label: "Hispanic / Latino?", type: "select", options: YN },
    { k: "countries_authorized", label: "Countries Authorized to Work In" },
    { k: "visa_status", label: "Visa Status and Type" }, { k: "visa_ending", label: "Visa Ending Date" },
    { k: "require_sponsorship", label: "Require Visa Sponsorship Now/Future?", type: "select", options: YN },
    { k: "prev_fed_gov", label: "Previously Employed by US Federal Gov/Military?", type: "select", options: YN },
    { k: "non_compete", label: "Subject to Non-Compete/Non-Solicit?", type: "select", options: YN },
    { k: "export_control", label: "Export Control (Iran/Cuba/NK/Syria/Crimea)?", type: "select", options: YN },
    { k: "security_clearance", label: "Active Security Clearance?", type: "select", options: YN },
    { k: "related_employee", label: "Related to a Current Employee?", type: "select", options: YN },
    { k: "driving_license", label: "Driving License?", type: "select", options: YN },
    { k: "companies_exclude", label: "Companies to Exclude from Applying" },
    { k: "employee_id", label: "Employee ID (if applicable)" },
  ]},
  { title: "Skills & Certifications", fields: [
    { k: "skills", label: "Skills (tools, languages, platforms)", type: "textarea" },
    { k: "certifications", label: "Certifications (Scrum, PMP, SAFe…)", type: "text" },
    { k: "reason_job_change", label: "Reason for Job Change", type: "text" },
  ]},
  { title: "Education", fields: [
    { k: "education", label: "Education (degrees, institutions, years)", type: "textarea" },
  ]},
  { title: "Additional", fields: [
    { k: "interest", label: "Interest in This Position", type: "textarea" },
    { k: "cover_letter", label: "Cover Letter (template)", type: "textarea" },
    { k: "notes", label: "Notes / anything else we should know", type: "textarea" },
  ]},
];

const TP_SECTIONS = [
  { title: "Job titles", fields: [
    { k: "primary_job_titles", label: "Primary titles", type: "arr" },
    { k: "secondary_job_titles", label: "Secondary titles", type: "arr" },
    { k: "adjacent_titles", label: "Adjacent titles", type: "arr" },
    { k: "titles_to_avoid", label: "Titles to avoid", type: "arr" },
  ]},
  { title: "Level & Salary", fields: [
    { k: "target_experience", label: "Target experience", type: "text" },
    { k: "target_seniority", label: "Target seniority", type: "text" },
    { k: "minimum_salary", label: "Minimum salary", type: "text" },
    { k: "preferred_salary", label: "Preferred salary", type: "text" },
  ]},
  { title: "Location & Arrangement", fields: [
    { k: "target_locations", label: "Target locations", type: "arr" },
    { k: "acceptable_locations", label: "Acceptable locations", type: "arr" },
    { k: "locations_to_avoid", label: "Locations to avoid", type: "arr" },
    { k: "work_arrangement", label: "Work arrangement rule", type: "text" },
    { k: "employment_type", label: "Employment type", type: "arr" },
  ]},
  { title: "Visa & Sponsorship", fields: [
    { k: "visa_sponsorship_requirement", label: "Requirement", type: "text" },
    { k: "sponsorship_keywords_target", label: "Keywords to target", type: "arr" },
    { k: "sponsorship_keywords_avoid", label: "Keywords to avoid", type: "arr" },
  ]},
  { title: "Industries", fields: [
    { k: "target_industries", label: "Target industries", type: "arr" },
    { k: "industries_to_avoid", label: "Industries to avoid", type: "arr" },
  ]},
  { title: "Skills", fields: [
    { k: "must_have_skills", label: "Must-have skills", type: "arr" },
    { k: "preferred_skills", label: "Preferred skills", type: "arr" },
    { k: "transferable_skills", label: "Transferable skills", type: "arr" },
  ]},
  { title: "Education & Companies", fields: [
    { k: "education_certifications", label: "Education / certifications", type: "arr" },
    { k: "target_companies", label: "Target companies", type: "arr" },
    { k: "companies_to_avoid", label: "Companies to avoid", type: "arr" },
  ]},
  { title: "Role types", fields: [
    { k: "role_types_to_target", label: "Role types to target", type: "arr" },
    { k: "role_types_to_avoid", label: "Role types to avoid", type: "arr" },
  ]},
  { title: "Screening rules", fields: [
    { k: "hard_skip_criteria", label: "Hard skip criteria", type: "arr" },
    { k: "borderline_criteria", label: "Borderline / review", type: "arr" },
    { k: "conflicts_or_notes", label: "Conflicts / notes", type: "arr" },
  ]},
];

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [showPwd, setShowPwd] = useState(false);
  const [client, setClient] = useState(null);
  const [intake, setIntake] = useState({});
  const [versions, setVersions] = useState([]);
  const [activeVid, setActiveVid] = useState(null);
  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState("");
  const [showResume, setShowResume] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [savingIntake, setSavingIntake] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const resumeRef = useRef();

  const active = versions.find((v) => v.id === activeVid) || versions[0] || null;

  const applyVersions = (list, keepId) => {
    setVersions(list);
    const av = list.find((v) => v.id === keepId) || list[0] || null;
    setActiveVid(av?.id || null);
    setProfile(av?.profile || null);
    setSummary(av?.profile_summary || "");
  };

  const load = (keepId) =>
    api.get(`/clients/${id}`).then((r) => {
      setClient(r.data);
      setIntake(r.data.intake || {});
      applyVersions(r.data.resumes || [], keepId ?? activeVid);
    });

  useEffect(() => { load(); }, [id]);

  const switchVersion = (vid) => {
    const v = versions.find((x) => x.id === vid);
    setActiveVid(vid);
    setProfile(v?.profile || null);
    setSummary(v?.profile_summary || "");
    setShowResume(false);
  };

  const setF = (k, v) => setIntake((p) => ({ ...p, [k]: v }));
  const toggleMulti = (k, opt) => setIntake((p) => {
    const cur = Array.isArray(p[k]) ? p[k] : (p[k] ? [p[k]] : []);
    return { ...p, [k]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
  });

  const addVersion = async () => {
    if (versions.length >= 4) { toast.error("You can have at most 4 resume versions"); return; }
    try {
      const { data } = await api.post(`/clients/${id}/resumes`, { label: `Version ${versions.length + 1}` });
      await load(data.id);
      toast.success("Resume version added");
    } catch (e) { toast.error(apiError(e)); }
  };

  const updateVersion = async (patch) => {
    if (!active) return;
    setVersions((vs) => vs.map((v) => (v.id === active.id ? { ...v, ...patch } : v)));
    try { await api.put(`/clients/${id}/resumes/${active.id}`, patch); } catch (e) { toast.error(apiError(e)); }
  };

  const deleteVersion = async () => {
    if (!active) return;
    if (versions.length <= 1) { toast.error("At least one resume version is required"); return; }
    if (!window.confirm(`Delete resume version "${active.label}"? This can't be undone.`)) return;
    try {
      await api.delete(`/clients/${id}/resumes/${active.id}`);
      await load();
      toast.success("Resume version deleted");
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadResume = async (file) => {
    if (!file || !active) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("resume", file);
    try {
      await api.post(`/clients/${id}/resumes/${active.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load(active.id);
      toast.success("Resume uploaded & text extracted");
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };

  const saveIntake = async () => {
    setSavingIntake(true);
    try {
      await api.put(`/clients/${id}`, { intake });
      toast.success("Intake form saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingIntake(false); }
  };

  const generate = async () => {
    if (!active) return;
    if (!active.resume_text) { toast.error("Upload the resume for this version first"); return; }
    if (active.profile && !window.confirm("Regenerate this version's targeting profile? Manual edits will be replaced.")) return;
    setGenerating(true);
    try {
      await api.put(`/clients/${id}`, { intake });
      const { data } = await api.post(`/clients/${id}/resumes/${active.id}/generate-profile`);
      setProfile(data.profile);
      setSummary(data.profile_summary || "");
      setVersions((vs) => vs.map((v) => (v.id === active.id ? { ...v, profile: data.profile, profile_summary: data.profile_summary || "" } : v)));
      toast.success("Targeting profile generated");
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenerating(false); }
  };

  const saveProfile = async () => {
    if (!active) return;
    setSavingProfile(true);
    try {
      await api.put(`/clients/${id}/resumes/${active.id}/profile`, { profile, profile_summary: summary });
      setVersions((vs) => vs.map((v) => (v.id === active.id ? { ...v, profile, profile_summary: summary } : v)));
      toast.success("Targeting profile saved — this drives evaluations for this version");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingProfile(false); }
  };

  const setP = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const [downloading, setDownloading] = useState(false);
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("ap_token");
      const res = await fetch(`${API}/clients/${id}/profile.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Failed to generate PDF");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(client?.name || "client").replace(/[^A-Za-z0-9_-]+/g, "_")}_targeting_profile.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(e.message || "Failed to generate PDF"); }
    finally { setDownloading(false); }
  };

  if (!client) return <div className="text-slate-500 text-sm">Loading…</div>;

  const chips = (arr, cls) => (arr || []).map((t) => (
    <span key={t} className={`text-xs px-2.5 py-1 rounded-full border ${cls}`}>{t}</span>
  ));

  return (
    <div className="space-y-6 animate-fade-up">
      <Link to="/clients" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 transition-colors">
        <ArrowLeft className="w-4 h-4" /> All clients
      </Link>

      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center text-indigo-300 font-bold text-xl">{client.name[0]}</div>
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-100">{client.name}</h1>
          <p className="text-sm text-slate-500">{client.email || "no email"} · {client.target_summary}</p>
        </div>
        <Link to={`/tracker?client=${id}`} className="ml-auto flex items-center gap-2 text-sm text-slate-300 bg-slate-800/60 hover:bg-slate-800 border border-slate-700/60 px-4 py-2 rounded-lg transition-colors">
          <ClipboardList className="w-4 h-4" /> Tracker
        </Link>
      </div>

      {/* Client login credentials (admin only) */}
      {isAdmin && client.has_login && (
        <div data-testid="client-credentials" className="bg-[#12151F] border border-indigo-500/25 rounded-xl p-5 shadow-lg shadow-black/40">
          <h3 className="font-heading font-semibold text-slate-100 flex items-center gap-2 mb-4"><KeyRound className="w-4 h-4 text-indigo-400" /> Client portal login</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Login email</div>
              <div className="flex items-center gap-2 bg-[#0D0E15] border border-slate-800 rounded-lg px-3 py-2">
                <span className="font-mono text-sm text-slate-200 truncate flex-1">{client.email || "—"}</span>
                {client.email && <button data-testid="copy-cred-email" onClick={() => { navigator.clipboard?.writeText(client.email); toast.success("Email copied"); }} className="text-slate-500 hover:text-slate-200"><Copy className="w-4 h-4" /></button>}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Password</div>
              <div className="flex items-center gap-2 bg-[#0D0E15] border border-slate-800 rounded-lg px-3 py-2">
                {client.login_password_plain ? (
                  <>
                    <span data-testid="cred-password" className="font-mono text-sm text-slate-200 truncate flex-1">{showPwd ? client.login_password_plain : "•".repeat(Math.min(client.login_password_plain.length, 12))}</span>
                    <button data-testid="toggle-cred-pwd" onClick={() => setShowPwd((s) => !s)} className="text-slate-500 hover:text-slate-200">{showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                    <button data-testid="copy-cred-pwd" onClick={() => { navigator.clipboard?.writeText(client.login_password_plain); toast.success("Password copied"); }} className="text-slate-500 hover:text-slate-200"><Copy className="w-4 h-4" /></button>
                  </>
                ) : (
                  <span className="text-sm text-slate-600 italic flex-1">Set before this update — reset it from the Clients page to view it here.</span>
                )}
              </div>
            </div>
          </div>
          {client.created_by_name && (
            <p className="text-[11px] text-slate-500 mt-3">Account created by <span className="text-slate-400">{client.created_by_name}</span>{client.created_by_role ? ` (${client.created_by_role})` : ""}.</p>
          )}
        </div>
      )}

      {/* Resume versions (up to 4) */}
      <div className="bg-[#12151F] border border-slate-800/80 rounded-xl p-5 shadow-lg shadow-black/40">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-heading font-semibold text-slate-100 flex items-center gap-2"><FileText className="w-4 h-4 text-indigo-400" /> Base resumes <span className="text-xs text-slate-500 font-normal">({versions.length}/4 versions — same intake, different target titles)</span></h3>
          <button data-testid="add-version-btn" onClick={addVersion} disabled={versions.length >= 4}
            className="flex items-center gap-1.5 text-sm bg-slate-800 hover:bg-slate-700 text-slate-100 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40">
            <Plus className="w-4 h-4" /> Add version
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          {versions.map((v) => (
            <button key={v.id} data-testid={`version-tab-${v.id}`} onClick={() => switchVersion(v.id)}
              className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg border transition-colors ${active?.id === v.id ? "bg-indigo-500/20 text-indigo-200 border-indigo-500/50" : "bg-slate-800/40 text-slate-400 border-slate-700/50 hover:border-slate-600"}`}>
              {v.label || "Version"}
              <span className={`w-1.5 h-1.5 rounded-full ${v.profile ? "bg-emerald-400" : "bg-slate-600"}`} />
            </button>
          ))}
        </div>

        {active && (
          <div className="mt-4 border-t border-slate-800/60 pt-4 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] text-slate-500">Version label</label>
                <input data-testid="version-label" value={active.label || ""} onChange={(e) => setVersions((vs) => vs.map((v) => v.id === active.id ? { ...v, label: e.target.value } : v))}
                  onBlur={(e) => updateVersion({ label: e.target.value })} className={`${inp} mt-1`} placeholder="e.g. Data Engineer" />
              </div>
              <div>
                <label className="text-[11px] text-slate-500">Target job titles for this version</label>
                <input data-testid="version-titles" value={active.target_titles || ""} onChange={(e) => setVersions((vs) => vs.map((v) => v.id === active.id ? { ...v, target_titles: e.target.value } : v))}
                  onBlur={(e) => updateVersion({ target_titles: e.target.value })} className={`${inp} mt-1`} placeholder="e.g. Data Engineer, ETL Engineer" />
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button data-testid="upload-resume-btn" onClick={() => resumeRef.current?.click()} className="flex items-center gap-1.5 text-sm bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg transition-colors">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload PDF / DOCX
              </button>
              <input ref={resumeRef} type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={(e) => uploadResume(e.target.files[0])} />
              {active.resume_text ? (
                <>
                  <span className="text-sm text-emerald-400">✓ {active.resume_filename || "Resume"} — {active.resume_text.length} chars</span>
                  <button onClick={() => setShowResume((s) => !s)} className="text-indigo-400 hover:text-indigo-300 text-xs flex items-center gap-1">
                    {showResume ? "Hide" : "View"} text <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showResume ? "rotate-180" : ""}`} />
                  </button>
                </>
              ) : <span className="text-sm text-slate-500">No resume for this version yet.</span>}
              {versions.length > 1 && (
                <button data-testid="delete-version-btn" onClick={deleteVersion} className="ml-auto flex items-center gap-1.5 text-sm text-slate-500 hover:text-rose-400 transition-colors">
                  <Trash2 className="w-4 h-4" /> Delete version
                </button>
              )}
            </div>
            {showResume && <textarea data-testid="resume-text" rows={8} readOnly value={active.resume_text || ""} className={`${inp} font-mono text-xs`} />}
          </div>
        )}
      </div>

      {/* Intake form */}
      <div className="bg-[#12151F] border border-slate-800/80 rounded-xl shadow-lg shadow-black/40">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800/60">
          <div>
            <h3 className="font-heading font-semibold text-slate-100">Intake form</h3>
            <p className="text-[11px] text-slate-500">Enter the client's details manually. This + the resume drive every evaluation.</p>
          </div>
          <button data-testid="save-intake-btn" onClick={saveIntake} disabled={savingIntake} className="flex items-center gap-2 text-sm bg-slate-800 hover:bg-slate-700 text-slate-100 px-4 py-2 rounded-lg transition-colors disabled:opacity-60">
            {savingIntake ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save intake
          </button>
        </div>
        <div className="p-6 space-y-7">
          {INTAKE_SECTIONS.map((sec) => (
            <div key={sec.title}>
              <div className="text-[11px] uppercase tracking-widest text-indigo-400 font-bold mb-3">{sec.title}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
                {sec.fields.map((f) => (
                  <div key={f.k} className={f.type === "textarea" ? "sm:col-span-2 lg:col-span-3" : ""}>
                    <label className="text-[11px] text-slate-500">{f.label}</label>
                    {f.type === "textarea" ? (
                      <textarea data-testid={`intake-${f.k}`} rows={2} value={intake[f.k] || ""} onChange={(e) => setF(f.k, e.target.value)} className={`${inp} mt-1`} />
                    ) : f.type === "select" ? (
                      <select data-testid={`intake-${f.k}`} value={intake[f.k] || ""} onChange={(e) => setF(f.k, e.target.value)} className={`${inp} mt-1`}>
                        {f.options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
                      </select>
                    ) : f.type === "multi" ? (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {f.options.map((o) => {
                          const cur = Array.isArray(intake[f.k]) ? intake[f.k] : (intake[f.k] ? [intake[f.k]] : []);
                          const on = cur.includes(o);
                          return (
                            <button key={o} type="button" data-testid={`intake-${f.k}-${o}`} onClick={() => toggleMulti(f.k, o)}
                              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-indigo-500/20 text-indigo-300 border-indigo-500/40" : "bg-slate-800/50 text-slate-400 border-slate-700/50 hover:border-slate-600"}`}>
                              {o}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <input data-testid={`intake-${f.k}`} value={intake[f.k] || ""} onChange={(e) => setF(f.k, e.target.value)} className={`${inp} mt-1`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Work experience */}
          <WorkExperience list={intake.work_experience || []} onChange={(we) => setF("work_experience", we)} />
        </div>
      </div>

      {/* Generate */}
      <div className="bg-gradient-to-r from-indigo-500/10 to-transparent border border-indigo-500/25 rounded-xl p-5 flex items-center gap-4 flex-wrap">
        <Target className="w-6 h-6 text-indigo-400" />
        <div className="flex-1 min-w-[220px]">
          <div className="font-heading font-semibold text-slate-100">Generate the targeting profile{active ? ` — ${active.label}` : ""}</div>
          <div className="text-xs text-slate-400">Runs the BulkJobApply master prompt on the shared intake + this version's resume. Each version has its own profile.</div>
        </div>
        <button data-testid="generate-profile-btn" onClick={generate} disabled={generating} className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors disabled:opacity-60">
          {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</> : <><Sparkles className="w-4 h-4" /> {profile ? "Regenerate profile" : "Generate profile"}</>}
        </button>
        {versions.some((v) => v.profile) && (
          <button data-testid="download-profile-pdf" onClick={downloadPdf} disabled={downloading} title="Download one PDF covering all resume versions"
            className="flex items-center gap-2 bg-slate-800/70 hover:bg-slate-800 text-slate-100 border border-slate-700/70 text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors disabled:opacity-60">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download PDF
          </button>
        )}
      </div>

      {/* Roles to target */}
      {profile && (
        <div data-testid="roles-to-target" className="bg-[#12151F] border border-slate-800/80 rounded-xl p-5 shadow-lg shadow-black/40">
          <h3 className="font-heading font-semibold text-slate-100 flex items-center gap-2"><Target className="w-4 h-4 text-emerald-400" /> Roles the executive can target</h3>
          <div className="mt-4 space-y-3">
            <RoleRow label="Primary" arr={profile.primary_job_titles} cls="bg-emerald-500/15 text-emerald-300 border-emerald-500/30" chips={chips} />
            <RoleRow label="Secondary" arr={profile.secondary_job_titles} cls="bg-indigo-500/15 text-indigo-300 border-indigo-500/30" chips={chips} />
            <RoleRow label="Adjacent" arr={profile.adjacent_titles} cls="bg-slate-800 text-slate-300 border-slate-700" chips={chips} />
            <RoleRow label="Avoid" arr={profile.titles_to_avoid} cls="bg-rose-500/15 text-rose-300 border-rose-500/30" chips={chips} />
          </div>
        </div>
      )}

      {/* Plain-language summary */}
      {profile && (
        <div className="bg-[#12151F] border border-slate-800/80 rounded-xl p-5 shadow-lg shadow-black/40">
          <h3 className="font-heading font-semibold text-slate-100 flex items-center gap-2"><MessageSquareQuote className="w-4 h-4 text-indigo-400" /> Plain-language summary</h3>
          <p className="text-[11px] text-slate-500 mt-1">Read this to the client to confirm you understood their goals.</p>
          <textarea data-testid="profile-summary" rows={4} value={summary} onChange={(e) => setSummary(e.target.value)} className={`${inp} mt-3 leading-relaxed`} />
        </div>
      )}

      {/* Editable targeting profile */}
      <div className="bg-[#12151F] border border-slate-800/80 rounded-xl shadow-lg shadow-black/40">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800/60">
          <div>
            <h3 className="font-heading font-semibold text-slate-100">Master targeting profile</h3>
            <p className="text-[11px] text-slate-500">Review & correct. The edited version is the single source of truth for evaluations.</p>
          </div>
          <div className="flex gap-2">
            {profile && (
              <button data-testid="regen-inline" onClick={generate} disabled={generating} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 px-3 py-2">
                <RefreshCw className={`w-3.5 h-3.5 ${generating ? "animate-spin" : ""}`} /> Regenerate
              </button>
            )}
            <button data-testid="save-profile-btn" onClick={saveProfile} disabled={savingProfile || !profile} className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50">
              {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save profile
            </button>
          </div>
        </div>

        {!profile ? (
          <div className="p-10 text-center text-sm text-slate-500">No targeting profile yet. Fill the intake form, upload the resume, then click <span className="text-indigo-400">Generate profile</span>.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-6 p-6">
            {TP_SECTIONS.map((sec) => (
              <div key={sec.title}>
                <div className="text-[11px] uppercase tracking-widest text-indigo-400 font-bold mb-3">{sec.title}</div>
                <div className="space-y-3">
                  {sec.fields.map((f) => (
                    <div key={f.k}>
                      <label className="text-[11px] text-slate-500">{f.label}</label>
                      {f.type === "arr" ? (
                        <textarea data-testid={`pf-${f.k}`} rows={Math.min(6, Math.max(2, (profile[f.k] || []).length))}
                          value={ARR(profile[f.k])} onChange={(e) => setP(f.k, toArr(e.target.value))}
                          placeholder="One per line" className={`${inp} mt-1 text-xs`} />
                      ) : (
                        <input data-testid={`pf-${f.k}`} value={profile[f.k] ?? ""} onChange={(e) => setP(f.k, e.target.value)} className={`${inp} mt-1`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RoleRow({ label, arr, cls, chips }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-20 shrink-0 text-xs text-slate-500 font-semibold pt-1">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {arr?.length ? chips(arr, cls) : <span className="text-xs text-slate-600">—</span>}
      </div>
    </div>
  );
}

function WorkExperience({ list, onChange }) {
  const update = (i, k, v) => { const c = [...list]; c[i] = { ...c[i], [k]: v }; onChange(c); };
  const add = () => onChange([...list, { company: "", start_date: "", end_date: "", position: "", location: "", responsibilities: "" }]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-widest text-indigo-400 font-bold">Work Experience</div>
        <button data-testid="add-work-exp" onClick={add} className="flex items-center gap-1 text-xs text-indigo-300 hover:text-indigo-200"><Plus className="w-3.5 h-3.5" /> Add role</button>
      </div>
      <div className="space-y-4">
        {list.map((w, i) => (
          <div key={i} className="bg-[#0D0E15] border border-slate-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-500 font-semibold">Role {i + 1}</span>
              <button data-testid={`remove-work-${i}`} onClick={() => remove(i)} className="text-slate-500 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {[["position", "Position"], ["company", "Company"], ["start_date", "Start Date"], ["end_date", "End Date"], ["location", "Location"]].map(([k, l]) => (
                <div key={k}><label className="text-[11px] text-slate-500">{l}</label>
                  <input data-testid={`work-${i}-${k}`} value={w[k] || ""} onChange={(e) => update(i, k, e.target.value)} className={`${inp} mt-1`} /></div>
              ))}
            </div>
            <div className="mt-3"><label className="text-[11px] text-slate-500">Responsibilities</label>
              <textarea data-testid={`work-${i}-responsibilities`} rows={2} value={w.responsibilities || ""} onChange={(e) => update(i, "responsibilities", e.target.value)} className={`${inp} mt-1`} /></div>
          </div>
        ))}
        {list.length === 0 && <div className="text-xs text-slate-600">No roles added yet.</div>}
      </div>
    </div>
  );
}
